import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import Icon from '~/components/Icon/Icon';
import { ROUTES } from '~/pages/Root/constants';

import BadgeCheckIcon from '../icons/BadgeCheckIcon';
import MessageCircleIcon from '../icons/MessageCircleIcon';

export interface TrustBarStat {
  icon: ReactNode;
  value: string;
  label: string;
}

interface Props {
  stats?: TrustBarStat[];
  linkHref?: string;
  linkLabel?: string;
  /**
   * `card` — standalone rounded pill used in page content (with the "About"
   * link). `footer` — full-width bar that frames the footer: centered content,
   * no link, large rounded top corners (48px desktop / 24px mobile).
   */
  variant?: 'card' | 'footer';
  flatTop?: boolean;
  className?: string;
}

const defaultStats: TrustBarStat[] = [
  {
    icon: <Icon type="shopping-bag" size={20} className="text-white" />,
    value: '377К',
    label: 'Проданных заказов',
  },
  {
    icon: <Icon type="star-fill" size={20} className="text-white" />,
    value: '5 из 5',
    label: 'Средняя оценка товаров',
  },
  {
    icon: <MessageCircleIcon className="size-5 text-white" />,
    value: '145К',
    label: 'Отзывов',
  },
  {
    icon: <BadgeCheckIcon className="size-5 text-white" />,
    value: 'с 2024 года',
    label: 'Продаем на Озоне',
  },
];

/**
 * Brand-orange bar with trust-building numbers (orders sold, average rating,
 * review count, years on the market). Data-driven (falls back to the default
 * DubaiExpress stats). The `card` variant is a standalone rounded pill with an
 * "About" link (home page); the `footer` variant frames the footer with a
 * centered, link-less bar and large rounded top corners.
 */
export const TrustBar = ({
  stats = defaultStats,
  linkHref = ROUTES.about,
  linkLabel = 'Подробнее о нас',
  variant = 'card',
  flatTop = false,
  className,
}: Props) => {
  const isFooter = variant === 'footer';

  return (
    <div
      className={cn(
        'flex bg-graphic-regular-brand',
        isFooter
          ? 'flex-col items-start gap-4 rounded-t-[24px] px-4 pt-5 pb-12 min-[1025px]:flex-row min-[1025px]:items-center min-[1025px]:justify-center min-[1025px]:gap-4 min-[1025px]:rounded-t-[48px] min-[1025px]:px-6 min-[1025px]:pt-4 min-[1025px]:pb-10'
          : 'flex-col gap-6 rounded-5 p-4 md:flex-row md:items-center md:justify-between md:gap-4 md:px-5 md:py-4',
        isFooter && flatTop && 'rounded-t-none min-[1025px]:rounded-t-none',
        className,
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-4',
          isFooter
            ? 'w-full min-[1025px]:w-auto min-[1025px]:flex-row min-[1025px]:items-center'
            : 'md:flex-row md:items-center',
        )}
      >
        {stats.map((stat, index) => (
          <Fragment key={stat.label}>
            {index > 0 && (
              <span
                className={cn(
                  'shrink-0 bg-graphic-transparent-white-55',
                  isFooter
                    ? 'h-px w-full min-[1025px]:h-5 min-[1025px]:w-px'
                    : 'h-px w-full md:h-5 md:w-px',
                )}
              />
            )}
            <div className="flex items-center gap-1.5">
              <span className="flex shrink-0 items-center">{stat.icon}</span>
              <Typography.Text300Semibold
                as="span"
                className="whitespace-nowrap text-white"
              >
                {stat.value}
              </Typography.Text300Semibold>
              <Typography.Text300Regular
                as="span"
                className="whitespace-nowrap text-white"
              >
                {stat.label}
              </Typography.Text300Regular>
            </div>
          </Fragment>
        ))}
      </div>
      {!isFooter && (
        <Link
          to={linkHref}
          className="flex items-center gap-1 text-white no-underline md:shrink-0"
        >
          <Typography.Text300Regular
            as="span"
            className="whitespace-nowrap text-white"
          >
            {linkLabel}
          </Typography.Text300Regular>
          <Icon type="arrow" size={20} className="text-white" />
        </Link>
      )}
    </div>
  );
};
